/**
 * 각주 하단 패널
 *
 * 에디터 하단에 각주 목록 패널을 생성하고 관리합니다.
 * - sup[data-footnote] 요소들과 양방향 동기화
 * - MutationObserver로 DOM 변경 감지 후 자동 갱신
 * - 각주 추가/삭제 시 번호 재부여
 */

let _panel = null;
let _observer = null;
let _refreshTimer = null;
let _isRefreshing = false;
let _holderElement = null;
let _editorClickHandler = null;
let _documentClickHandler = null;
let _resizeHandler = null;
let _stickyLock = false;

/**
 * 각주 패널을 초기화합니다.
 * @param {HTMLElement} holderElement - 에디터 홀더 엘리먼트
 * @returns {Function} cleanup 함수
 */
export function setupFootnotePanel(holderElement) {
  _holderElement = holderElement;

  // 패널 DOM 생성
  _panel = document.createElement('div');
  _panel.className = 'cdx-footnote-panel';
  _panel.style.display = 'none';
  _panel.innerHTML = `
    <div class="cdx-footnote-panel__header">
      <span>각주</span>
      <button type="button" class="cdx-footnote-panel__close">닫기</button>
    </div>
    <div class="cdx-footnote-panel__list"></div>
  `;
  holderElement.after(_panel);

  // 닫기 버튼
  _panel.querySelector('.cdx-footnote-panel__close').addEventListener('click', () => {
    _makePanelNormal();
  });

  // 에디터 클릭 delegation: 각주 클릭 → sticky + focus, 그 외 → normal
  _editorClickHandler = (e) => {
    // footnote:changed 직후 lock이 걸린 상태면 sticky 해제 방지
    // (inlineToolbar.close()가 DOM을 분리시켜 closest 체크가 실패하는 문제 방어)
    if (_stickyLock) return;

    const sup = e.target.closest('sup[data-footnote]');
    if (sup) {
      _makePanelSticky();
      _focusPanelEntry(sup);
    } else {
      _makePanelNormal();
    }
  };
  holderElement.addEventListener('click', _editorClickHandler);

  // 에디터/패널 외부 클릭 시 sticky 해제
  _documentClickHandler = (e) => {
    if (_stickyLock) return;
    if (!_panel?.classList.contains('cdx-footnote-panel--sticky')) return;
    if (_holderElement?.contains(e.target) || _panel?.contains(e.target)) return;
    _makePanelNormal();
  };
  document.addEventListener('click', _documentClickHandler);

  // footnote:changed 이벤트 리스너
  const handleChanged = (e) => {
    refreshFootnotePanel();
    if (e.detail?.focusIndex != null) {
      _makePanelSticky();
      // 클릭 이벤트 버블링이 sticky를 해제하지 못하도록 잠시 lock
      _stickyLock = true;
      setTimeout(() => {
        _stickyLock = false;
        _showOnlyEntry(e.detail.focusIndex);
        const entries = _panel.querySelectorAll('.cdx-footnote-panel__entry');
        const entry = entries[e.detail.focusIndex];
        if (entry) {
          entry.querySelector('input')?.focus();
        }
      }, 50);
    }
  };
  document.addEventListener('footnote:changed', handleChanged);

  // MutationObserver: 에디터 내 DOM 변경 시 각주 개수 확인
  const editorEl = holderElement.querySelector('.codex-editor') || holderElement;
  let lastCount = editorEl.querySelectorAll('sup[data-footnote]').length;

  _observer = new MutationObserver(() => {
    if (_isRefreshing) return;
    clearTimeout(_refreshTimer);
    _refreshTimer = setTimeout(() => {
      const currentCount = editorEl.querySelectorAll('sup[data-footnote]').length;
      if (currentCount !== lastCount) {
        lastCount = currentCount;
        refreshFootnotePanel();
      }
    }, 150);
  });
  _observer.observe(editorEl, { childList: true, subtree: true });

  // 초기 갱신
  refreshFootnotePanel();

  // cleanup 함수 반환
  return () => {
    document.removeEventListener('footnote:changed', handleChanged);
    if (_editorClickHandler) {
      holderElement.removeEventListener('click', _editorClickHandler);
      _editorClickHandler = null;
    }
    if (_documentClickHandler) {
      document.removeEventListener('click', _documentClickHandler);
      _documentClickHandler = null;
    }
    _makePanelNormal();
    if (_observer) {
      _observer.disconnect();
      _observer = null;
    }
    clearTimeout(_refreshTimer);
    if (_panel && _panel.parentElement) {
      _panel.remove();
    }
    _panel = null;
    _holderElement = null;
  };
}

/**
 * 각주 패널을 현재 DOM 상태에 맞게 갱신합니다.
 */
export function refreshFootnotePanel() {
  if (!_panel || _isRefreshing) return;
  _isRefreshing = true;

  try {
    const editorEl = document.querySelector('.codex-editor');
    if (!editorEl) return;

    const sups = editorEl.querySelectorAll('sup[data-footnote]');
    const list = _panel.querySelector('.cdx-footnote-panel__list');

    if (sups.length === 0) {
      _panel.style.display = 'none';
      list.innerHTML = '';
      return;
    }

    _panel.style.display = 'block';

    // 현재 포커스 위치 보존
    const activeEl = list.querySelector('input:focus');
    let activeIndex = -1;
    let activeCursor = 0;
    if (activeEl) {
      const entries = list.querySelectorAll('.cdx-footnote-panel__entry');
      activeIndex = Array.from(entries).indexOf(activeEl.closest('.cdx-footnote-panel__entry'));
      activeCursor = activeEl.selectionStart || 0;
    }

    list.innerHTML = '';

    sups.forEach((sup, index) => {
      const num = index + 1;

      // sup 텍스트 및 속성 동기화
      sup.textContent = `[${num}]`;
      sup.contentEditable = 'false';

      // 패널 항목 생성
      const entry = document.createElement('div');
      entry.className = 'cdx-footnote-panel__entry';

      const label = document.createElement('span');
      label.className = 'cdx-footnote-panel__number';
      label.textContent = `[${num}]`;

      const input = document.createElement('input');
      input.className = 'cdx-footnote-panel__input';
      input.type = 'text';
      input.value = sup.dataset.footnote || '';
      input.placeholder = '각주 내용을 입력하세요';
      input.addEventListener('input', () => {
        sup.dataset.footnote = input.value;
      });
      // 패널 input에서 에디터 단축키가 실행되지 않도록
      input.addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          // IME 조합 중이면 무시 (조합 확정만 수행, 패널 닫기는 다음 Enter에서)
          if (e.isComposing) return;
          e.preventDefault();
          input.blur();
          _makePanelNormal();
          requestAnimationFrame(() => _restoreCursorAfterSup(sup));
        }
      });

      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.className = 'cdx-footnote-panel__delete';
      deleteBtn.textContent = '×';
      deleteBtn.title = '삭제';
      deleteBtn.addEventListener('click', () => {
        // sup 뒤 ZWS 제거
        const next = sup.nextSibling;
        if (next?.nodeType === Node.TEXT_NODE && next.textContent.startsWith('\u200B')) {
          next.textContent = next.textContent.substring(1);
          if (!next.textContent) next.remove();
        }
        sup.remove();
        refreshFootnotePanel();
      });

      entry.appendChild(label);
      entry.appendChild(input);
      entry.appendChild(deleteBtn);
      list.appendChild(entry);
    });

    // 포커스 복원
    if (activeIndex >= 0) {
      const entries = list.querySelectorAll('.cdx-footnote-panel__entry');
      const restoreEntry = entries[Math.min(activeIndex, entries.length - 1)];
      if (restoreEntry) {
        const input = restoreEntry.querySelector('input');
        input?.focus();
        try { input.setSelectionRange(activeCursor, activeCursor); } catch (_) {}
      }
    }
  } finally {
    _isRefreshing = false;
  }
}

/**
 * 패널을 viewport 하단에 고정합니다.
 */
function _makePanelSticky() {
  if (!_panel || !_holderElement) return;
  if (_panel.classList.contains('cdx-footnote-panel--sticky')) return;

  _panel.classList.add('cdx-footnote-panel--sticky');
  _updateStickyPosition();

  _resizeHandler = () => _updateStickyPosition();
  window.addEventListener('resize', _resizeHandler);
}

function _updateStickyPosition() {
  if (!_panel || !_holderElement) return;
  const rect = _holderElement.getBoundingClientRect();
  _panel.style.left = `${rect.left}px`;
  _panel.style.width = `${rect.width}px`;
}

/**
 * 패널을 원래 위치로 복귀합니다.
 */
function _makePanelNormal() {
  if (!_panel) return;
  _panel.classList.remove('cdx-footnote-panel--sticky');
  _panel.style.left = '';
  _panel.style.width = '';
  _showAllEntries();

  if (_resizeHandler) {
    window.removeEventListener('resize', _resizeHandler);
    _resizeHandler = null;
  }
}

/**
 * 패널에서 해당 sup에 대응하는 input을 포커스합니다.
 */
function _focusPanelEntry(sup) {
  if (!_panel) return;
  const editorEl = document.querySelector('.codex-editor');
  if (!editorEl) return;

  const sups = Array.from(editorEl.querySelectorAll('sup[data-footnote]'));
  const index = sups.indexOf(sup);
  if (index < 0) return;

  _showOnlyEntry(index);
  const entries = _panel.querySelectorAll('.cdx-footnote-panel__entry');
  if (entries[index]) {
    entries[index].querySelector('input')?.focus();
  }
}

/**
 * sticky 상태에서 특정 entry만 보이게 합니다.
 */
function _showOnlyEntry(index) {
  if (!_panel) return;
  const entries = _panel.querySelectorAll('.cdx-footnote-panel__entry');
  entries.forEach((entry, i) => {
    entry.style.display = i === index ? '' : 'none';
  });
}

/**
 * sup 요소 바로 뒤로 커서를 복원합니다.
 */
function _restoreCursorAfterSup(sup) {
  if (!sup || !sup.parentElement || !document.body.contains(sup)) return;
  try {
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    const next = sup.nextSibling;
    if (next?.nodeType === Node.TEXT_NODE) {
      range.setStart(next, next.textContent.startsWith('\u200B') ? 1 : 0);
    } else {
      range.setStartAfter(sup);
    }
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch { /* DOM 교체 시 무시 */ }
}

/**
 * 모든 entry를 다시 보이게 합니다.
 */
function _showAllEntries() {
  if (!_panel) return;
  const entries = _panel.querySelectorAll('.cdx-footnote-panel__entry');
  entries.forEach((entry) => {
    entry.style.display = '';
  });
}

/**
 * 패널이 sticky 상태인지 반환합니다.
 */
export function isPanelSticky() {
  return _panel?.classList.contains('cdx-footnote-panel--sticky') || false;
}

/**
 * 각주 패널을 제거합니다.
 */
export function destroyFootnotePanel() {
  if (_panel && _panel.parentElement) {
    _panel.remove();
  }
  _panel = null;
}
